/**
 * Email Validate Tool
 *
 * Validate email addresses against RFC 5322 simplified rules.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITransformerTool } from '@/interfaces/ITool';

// RFC 5322 simplified: local-part@domain
const LOCAL_PART_RE = /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*$/;
const DOMAIN_LABEL_RE = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;

function validateEmail(email: string): { valid: boolean; error?: string; parts?: { local: string; domain: string; tld: string } } {
  const trimmed = email.trim();

  if (!trimmed) return { valid: false, error: 'Empty input' };
  if (trimmed.length > 254) return { valid: false, error: 'Email exceeds 254 character limit' };

  const atIndex = trimmed.lastIndexOf('@');
  if (atIndex < 1) return { valid: false, error: 'Missing @ symbol or empty local part' };
  if (atIndex === trimmed.length - 1) return { valid: false, error: 'Missing domain after @' };

  const local = trimmed.slice(0, atIndex);
  const domain = trimmed.slice(atIndex + 1);

  if (local.length > 64) return { valid: false, error: `Local part exceeds 64 characters (got ${local.length})` };
  if (!LOCAL_PART_RE.test(local)) return { valid: false, error: 'Invalid characters in local part' };

  if (domain.length > 253) return { valid: false, error: `Domain exceeds 253 characters (got ${domain.length})` };

  const labels = domain.split('.');
  if (labels.length < 2) return { valid: false, error: 'Domain must have at least two labels (e.g., example.com)' };

  for (const label of labels) {
    if (label.length === 0) return { valid: false, error: 'Domain contains empty label (consecutive dots)' };
    if (label.length > 63) return { valid: false, error: `Domain label "${label}" exceeds 63 characters` };
    if (!DOMAIN_LABEL_RE.test(label)) return { valid: false, error: `Invalid domain label "${label}"` };
  }

  const tld = labels[labels.length - 1];
  if (/^\d+$/.test(tld)) return { valid: false, error: 'TLD cannot be all numeric' };

  return { valid: true, parts: { local, domain, tld } };
}

export const EmailValidateTool: ITransformerTool = {
  id: 'email-validate',
  name: 'Email Validate',
  description: 'Validate email addresses (RFC 5322 simplified)',
  version: '1.0.0',
  type: 'transformer',
  category: 'Validators & Testers',
  keywords: ['email', 'validate', 'check', 'verify', 'rfc5322', 'address'],

  seo: {
    slug: 'email-validator',
    pageTitle: 'Email Validator - Free Online RFC 5322 Checker',
    metaDescription: 'Validate email addresses against RFC 5322 rules. Check local part, domain, and TLD with detailed error messages. Fast, private, runs in your browser.',
    keywords: ['email validator', 'email checker', 'validate email online', 'rfc 5322 email', 'email syntax checker'],
    relatedTools: ['extract-emails', 'url-validate'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [],

  async transform(input: string): Promise<string> {
    const lines = input.trim().split('\n').map(l => l.trim()).filter(Boolean);
    const results: string[] = [];

    for (const line of lines) {
      const result = validateEmail(line);
      if (result.valid && result.parts) {
        results.push(
          `✓ ${line}\n  Local: ${result.parts.local}\n  Domain: ${result.parts.domain}\n  TLD: ${result.parts.tld}`
        );
      } else {
        results.push(`✗ ${line}\n  Error: ${result.error}`);
      }
    }

    return results.join('\n\n');
  },
};
